import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { BOOKING_URL } from "../data/site";
import { track } from "../lib/analytics";
import logoMark from "../assets/zentryx-mark.png";

const INDUSTRIES_MENU = [
  { label: "IT for CPA & Accounting Firms", to: "/industries/cpa-accounting-firms-atlanta" },
  { label: "IT for Law Firms", to: "/industries/law-firms" },
  { label: "IT for Healthcare Practices", to: "/industries/healthcare-practices" },
  { label: "IT for Architecture Firms", to: "/industries/architecture-engineering-firms-atlanta" },
  { label: "IT for Engineering Firms", to: "/industries/managed-it-for-engineering-firms-atlanta" },
  { label: "IT for Professional Services", to: "/industries/professional-services" },
  { label: "IT for Financial Services", to: "/industries/financial-services" },
];

const RESOURCES_MENU = [
  { label: "How to Choose the Best MSP in Atlanta", to: "/resources/how-to-choose-best-msp-atlanta" },
  { label: "Why Atlanta Businesses Choose Zentryx", to: "/resources/why-atlanta-businesses-choose-zentryx" },
  { label: "What CPA Firms Should Look for in an MSP", to: "/resources/what-atlanta-cpa-firms-should-look-for-in-an-msp" },
  { label: "What Architecture Firms Should Look For", to: "/resources/what-architecture-firms-should-look-for-in-an-it-partner" },
  { label: "What Engineering Firms Should Look For", to: "/resources/what-engineering-firms-should-look-for-in-an-it-provider" },
  { label: "View All Resources", to: "/resources" },
];

const TOP_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileGroup(null); }, [pathname]);

  const bookClick = () => track("book_intro_call_click", { location: "navbar" });

  const Dropdown = ({ label, items, testid }) => (
    <div className="relative group">
      <button data-testid={testid} className="px-4 py-2 text-sm font-medium rounded-full text-slate-600 hover:text-navy inline-flex items-center gap-1 transition-colors">
        {label} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2 w-72">
          {items.map((it) => (
            <Link key={it.to} to={it.to}
              className="block px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-mist hover:text-navy transition-colors">
              {it.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <img src={logoMark} alt="Zentryx Systems" className="h-9 w-auto" />
          <span className="font-heading font-extrabold text-lg tracking-tight text-charcoal">
            Zentryx<span className="text-teal"> Systems</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {TOP_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${pathname === l.to ? "text-navy" : "text-slate-600 hover:text-navy"}`}>
              {l.label}
            </Link>
          ))}
          <Dropdown label="Industries" items={INDUSTRIES_MENU} testid="nav-industries" />
          <Dropdown label="Resources" items={RESOURCES_MENU} testid="nav-resources" />
          <Link to="/about" data-testid="nav-about" className="px-4 py-2 text-sm font-medium rounded-full text-slate-600 hover:text-navy transition-colors">About</Link>
          <Link to="/contact" data-testid="nav-contact" className="px-4 py-2 text-sm font-medium rounded-full text-slate-600 hover:text-navy transition-colors">Contact</Link>
        </div>

        <div className="hidden lg:block">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={bookClick}
            data-testid="nav-booking-button"
            className="inline-flex items-center px-5 py-2.5 rounded-full bg-emerald hover:bg-emerald-dark text-white text-sm font-semibold transition-colors">
            Book an Intro Call
          </a>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-charcoal" aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-b border-slate-200 px-5 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
          {TOP_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-mist">{l.label}</Link>
          ))}
          {[["Industries", INDUSTRIES_MENU], ["Resources", RESOURCES_MENU]].map(([label, items]) => (
            <div key={label}>
              <button onClick={() => setMobileGroup((g) => (g === label ? null : label))}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-mist">
                {label} <ChevronDown className={`h-4 w-4 transition-transform ${mobileGroup === label ? "rotate-180" : ""}`} />
              </button>
              {mobileGroup === label && (
                <div className="pl-3 space-y-0.5">
                  {items.map((it) => (
                    <Link key={it.to} to={it.to} className="block px-4 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-mist">{it.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/about" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-mist">About</Link>
          <Link to="/contact" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-mist">Contact</Link>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={bookClick}
            className="block text-center mt-2 px-5 py-3 rounded-full bg-emerald text-white font-semibold">Book an Intro Call</a>
        </div>
      )}
    </header>
  );
};
